"use client";

import { AgentVerifiedBadge } from "@/components/agent-profile/agent-verified-badge";
import { BrokerAvatar } from "@/components/brokers/broker-avatar";
import type { Agent } from "@/lib/types";
import { displayBrokerageName } from "@repo/utils";
import { differenceInYears } from "date-fns";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";

export type LargeAgentCardProps = {
  agent: Agent;
  displayBio?: boolean;
  trailing?: ReactNode;
};

export function LargeAgentCard({
  agent,
  displayBio = true,
  trailing,
}: LargeAgentCardProps) {
  const t = useTranslations();

  const name = agent?.name ?? agent?.email ?? "";
  const brokerage = displayBrokerageName(agent.profile);

  return (
    <article className="border rounded-md p-6 flex flex-col gap-6">
      <div className="flex gap-4 items-center">
        <BrokerAvatar
          id={agent.id}
          name={name}
          image={agent.image}
          isVerified={agent.isVerified}
          size="xl"
          className="!size-16 md:!size-[92px]"
        />

        <div className="flex-1 flex flex-col gap-1">
          <div className="flex items-center gap-2 text-2xl font-semibold">
            {name}
            <AgentVerifiedBadge agent={agent} />
          </div>

          {brokerage && (
            <div className="text-muted-foreground">
              {brokerage}
            </div>
          )}

          {agent.profile && agent.profile.languages.length > 0 && (
            <div className="text-muted-foreground">
              {t("saas.create.agentProfile.languages", {
                languages: agent.profile.languages
                  .map((l) => l.language.name)
                  .join(", "),
              })}
            </div>
          )}

          {agent.profile?.agentExperienceStartDate && (
            <div className="text-muted-foreground">
              {t("saas.create.agentProfile.experience", {
                years: differenceInYears(
                  new Date(),
                  agent.profile.agentExperienceStartDate,
                ),
              })}
            </div>
          )}
        </div>
      </div>

      {displayBio && agent.profile?.bio && (
        <div className="bg-foreground/3 p-6 rounded-md">
          {agent.profile.bio}
        </div>
      )}

      {trailing}
    </article>
  );
}

LargeAgentCard.displayName = "LargeAgentCard";
